/* ================================================================
   contactService.ts — sends Contact-page enquiries to the JurisTab
   Contact Us backend (juristab-backend-integration/contact-us), which
   emails them to the market's contact address with Reply-To set to the
   visitor.

   Same-origin /api/proxy like the calculator and auth flows: the proxy
   injects the API-Gateway x-api-key server-side.
   ================================================================ */

import { apiPost, ApiError } from './api';
import { REGION } from '../config/region';

const ENQUIRIES_ENDPOINT = 'integration-contact-us/enquiries';

export interface EnquiryPayload {
  first_name: string;
  last_name: string;
  email: string;
  phone?: string;
  /** Slug of the subject option, e.g. "sales-pricing". */
  subject: string;
  message: string;
  /** Honeypot — always empty for a person. */
  website?: string;
}

export interface EnquiryResult {
  ok: boolean;
  reference?: string;
  message: string;
}

const FALLBACK_ERROR = `We couldn't send your message just now. Please try again, or email ${REGION.contact.email}.`;

export async function sendEnquiry(payload: EnquiryPayload): Promise<EnquiryResult> {
  try {
    const { data } = await apiPost(ENQUIRIES_ENDPOINT, payload);
    return data as EnquiryResult;
  } catch (err) {
    if (err instanceof ApiError) {
      // No status: network failure or timeout — api.ts already wrote the message.
      if (!err.status) throw new Error(err.message);
      // 429 / 503 carry a sentence written for the visitor.
      if (typeof err.data?.message === 'string') throw new Error(err.data.message);
      // 422: FastAPI's list of field errors — surface the first readable one.
      const detail = err.data?.detail;
      if (Array.isArray(detail) && detail.length) {
        const field = String(detail[0]?.loc?.[detail[0].loc.length - 1] ?? '').replace(/_/g, ' ');
        const msg = String(detail[0]?.msg ?? '').replace(/^Value error, /, '');
        if (field && msg) throw new Error(`Please check the ${field}: ${msg}.`);
      }
      throw new Error(FALLBACK_ERROR);
    }
    throw new Error(FALLBACK_ERROR);
  }
}
